import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/layout/Layout';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Home from './pages/Home';
import Explore from './pages/Explore';
import ArtworkDetail from './pages/ArtworkDetail';
import PostArtwork from './pages/PostArtwork';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Orders from './pages/Orders';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import Promotion from './pages/Promotion';
import Notifications from './pages/Notifications';
import Analytics from './pages/Analytics';
import Referrals from './pages/Referrals';
import AdminDashboard from './pages/Admin/Dashboard';
import ManageUsers from './pages/Admin/ManageUsers';
import ManageArtworks from './pages/Admin/ManageArtworks';
import Reports from './pages/Admin/Reports';
import { Loader } from './components/common/Loader';
import SellerDashboard from './pages/Seller/Dashboard';
import SellerOrders from './pages/Seller/Orders';
import AcceptOrder from './pages/Seller/AcceptOrder';
import OrderDetail from './pages/Seller/OrderDetail';
import AddPhone from './pages/AddPhone';
import PrivacyPolicy from './pages/PrivacyPolicy';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <Loader />
      </div>
    );
  }
  
  if (!user) return <Navigate to="/signin" />;
  
  return <>{children}</>;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <Loader />
      </div>
    );
  }

  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/signin" element={!user ? <SignIn /> : <Navigate to="/" />} />
      <Route path="/signup" element={!user ? <SignUp /> : <Navigate to="/" />} />
      
      {/* Main Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Home />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/explore"
        element={
          <ProtectedRoute>
            <Layout>
              <Explore />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/artwork/:id"
        element={
          <ProtectedRoute>
            <Layout showBottomNav={false}>
              <ArtworkDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/post"
        element={
          <ProtectedRoute>
            <Layout showBottomNav={false}>
              <PostArtwork />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/cart"
        element={
          <ProtectedRoute>
            <Layout>
              <Cart />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        }
      />

      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile/:userId"
        element={
          <ProtectedRoute>
            <Layout showBottomNav={false}>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/edit-profile"
        element={
          <ProtectedRoute>
            <EditProfile />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/promotion"
        element={
          <ProtectedRoute>
            <Layout showBottomNav={false}>
              <Promotion />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />

      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <Referrals />
          </ProtectedRoute>
        }
      />
      
     
      
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Layout showBottomNav={false}>
              <Notifications />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Seller Routes */}
<Route
  path="/seller/dashboard"
  element={
    <ProtectedRoute>
      <SellerDashboard />
    </ProtectedRoute>
  }
/>

<Route
  path="/seller/orders"
  element={
    <ProtectedRoute>
      <SellerOrders />
    </ProtectedRoute>
  }
/>

<Route
  path="/seller/orders/:orderId/accept"
  element={
    <ProtectedRoute>
      <AcceptOrder />
    </ProtectedRoute>
  }
/>

<Route
  path="/seller/orders/:orderId"
  element={
    <ProtectedRoute>
      <Layout showBottomNav={false}>
        <OrderDetail />
      </Layout>
    </ProtectedRoute>
  }
/>

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <ManageUsers />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/artworks"
        element={
          <ProtectedRoute>
            <ManageArtworks />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />

      <Route
        path="/add-phone"
        element={
          <ProtectedRoute>
            <AddPhone />
          </ProtectedRoute>
        }
      />


      {/* Public Routes */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;